import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Card, CardContent, Chip,
    IconButton, Tabs, Tab, InputAdornment, Avatar, AvatarGroup,
    Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
    Select, MenuItem, OutlinedInput, Checkbox, ListItemText,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, ToggleButton, ToggleButtonGroup, Autocomplete,
    Menu, CircularProgress, Grow, Divider, Tooltip
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

function Tickets() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createLoading, setCreateLoading] = useState(false);
    const [filter, setFilter] = useState('all'); // all, assigned_me, unassigned, closed

    // Keep track of filter for async operations
    const filterRef = React.useRef(filter);
    useEffect(() => { filterRef.current = filter; }, [filter]);

    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [open, setOpen] = useState(false);
    
    const token = localStorage.getItem('token');
    // Parse userId immediately for default state
    const storedUserId = localStorage.getItem('user_id');
    const initialAssignedToIds = storedUserId ? [parseInt(storedUserId)] : [];

    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [itProjects, setItProjects] = useState([]);
    const [types, setTypes] = useState([]);
    const [countries, setCountries] = useState([]);
    const [newTicket, setNewTicket] = useState({
        title: '',
        description: '',
        problem: '',
        recommendation: '',
        status: 'Open',
        priority: 'Medium',
        requested_by: '',
        sender_email: '',
        on_behalf_name: '',
        on_behalf_email: '',
        project_id: '',
        it_project_id: '',
        ticket_type_id: '',
        country_id: '',
        assigned_to_ids: initialAssignedToIds,
        due_date: new Date().toISOString().split('T')[0]
    });
    const navigate = useNavigate();
    const location = useLocation();

    const userId = storedUserId;
    const headers = { Authorization: `Bearer ${token}` };

    const [assignedToMe, setAssignToMe] = useState(true);
    const [selectedCountries, setSelectedCountries] = useState([]);

    // Status Menu State
    const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
    const [statusMenuTicketId, setStatusMenuTicketId] = useState(null);

    // AD Search State
    const [adUsers, setAdUsers] = useState([]);
    const [adLoading, setAdLoading] = useState(false);

    const handleADSearch = async (query) => {
        if (!query || query.length < 3) {
            setAdUsers([]);
            return;
        }
        setAdLoading(true);
        try {
            const res = await axios.get(`${API_URL}/tickets/search-ad-users?q=${query}`, { headers });
            setAdUsers(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setAdLoading(false);
        }
    };

    const handleStatusClick = (event, ticketId) => {
        event.stopPropagation();
        setStatusMenuAnchor(event.currentTarget);
        setStatusMenuTicketId(ticketId);
    };

    const handleStatusClose = () => {
        setStatusMenuAnchor(null);
        setStatusMenuTicketId(null);
    };

    const handleStatusUpdate = async (newStatus) => {
        if (!statusMenuTicketId) return;
        try {
            await axios.put(`${API_URL}/tickets/${statusMenuTicketId}`, { status: newStatus }, { headers });
            fetchTickets();
        } catch (e) { console.error(e); }
        handleStatusClose();
    };

    const getCurrentUser = () => {
        if (!token) return null;
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            return null;
        }
    };
    const currentUser = getCurrentUser();

    // Ref to track if sync has already started (prevents double sync in StrictMode)
    const syncStartedStr = React.useRef(false);

    const fetchTickets = async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            // 2. Fetch updated tickets
            const currentFilter = filterRef.current;
            const params = { user_id: userId, filter: currentFilter };
            const res = await axios.get(`${API_URL}/tickets`, { headers, params });
            setTickets(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const syncEmails = async () => {
        try {
            if (syncStartedStr.current) return;
            syncStartedStr.current = true;

            await axios.post(`${API_URL}/tickets/sync-emails`, {}, { headers });
            // After sync, refresh tickets to show any new ones
            fetchTickets();
        } catch (err) {
            console.error("Sync failed", err);
        }
    };

    const fetchMeta = async () => {
        try {
            const [usersRes, projectsRes, itProjectsRes, typesRes, countriesRes] = await Promise.all([
                axios.get(`${API_URL}/team`, { headers }),
                axios.get(`${API_URL}/projects`, { headers }),
                axios.get(`${API_URL}/it-projects`, { headers }),
                axios.get(`${API_URL}/ticket-types`, { headers }),
                axios.get(`${API_URL}/setup/countries`, { headers })
            ]);
            setUsers(usersRes.data);
            setProjects(projectsRes.data);
            setItProjects(itProjectsRes.data);
            setTypes(typesRes.data);
            setCountries(countriesRes.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchTickets();
        fetchMeta();

        // Refresh tickets every 30 seconds to catch new emails from the background script
        const interval = setInterval(() => {
            fetchTickets(false); // Silent refresh
        }, 30000);

        return () => clearInterval(interval);
    }, [filter]);



    useEffect(() => {
        if (location.state && location.state.createFromEmail) {
            const { title, description, requested_by, sender_email } = location.state.createFromEmail;

            // Convert HTML body to plain text if possible, or just use it.
            // For simplicity, we just use the raw body (which might be HTML)
            // If the backend expects plain text, we might want to strip tags here or in InboxDetails

            // Create a temporary element to strip HTML tags for the description preview if needed
            let plainTextDescription = description;
            try {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = description;
                plainTextDescription = tempDiv.textContent || tempDiv.innerText || "";
            } catch (e) {
                console.log("Error parsing HTML description", e);
            }

            setNewTicket(prev => ({
                ...prev,
                title: title || '',
                description: plainTextDescription || '',
                requested_by: requested_by || '',
                sender_email: sender_email || ''
            }));
            setOpen(true);

            // Clear state to prevent reopening on refresh (requires navigating)
            // But doing navigate replace here might trigger re-render loops if not careful.
            // For now, we leave it or we can replace history.
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handleCreate = async () => {
        setCreateLoading(true);
        try {
            const payload = { ...newTicket, created_by_id: userId };
            await axios.post(`${API_URL}/tickets`, payload, { headers });
            setOpen(false);
            fetchTickets();
            setNewTicket({
                title: '', description: '', problem: '', recommendation: '', status: 'Open', priority: 'Medium',
                requested_by: '', sender_email: '', on_behalf_name: '', on_behalf_email: '', project_id: '', it_project_id: '', ticket_type_id: '',
                assigned_to_ids: initialAssignedToIds, due_date: new Date().toISOString().split('T')[0]
            });
        } catch (err) {
            alert('Error creating ticket');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setFilter(newValue);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Done': return 'success';
            case 'In Progress': return 'info';
            case 'Open': return 'warning';
            case 'Reopen': return 'error';
            default: return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Critical': return 'error';
            case 'High': return 'warning';
            case 'Medium': return 'info';
            case 'Low': return 'success';
            default: return 'default';
        }
    };

    const filteredTickets = tickets.filter(t => {
        const matchesSearch = t.title.toLowerCase().includes(search.toLowerCase()) ||
            (t.type && t.type.toLowerCase().includes(search.toLowerCase())) ||
            (t.requested_by && t.requested_by.toLowerCase().includes(search.toLowerCase())) ||
            (t.on_behalf_name && t.on_behalf_name.toLowerCase().includes(search.toLowerCase())) ||
            (String(t.id).includes(search));

        const matchesCountry = selectedCountries.length === 0 || selectedCountries.includes(t.country_name);

        // For assignments, t.assigned_to is now array of objects {id, full_name, username}
        const matchesAssign = !assignedToMe || (t.assigned_to && t.assigned_to.some(u => u.id === currentUser?.user_id || u.id === parseInt(userId)));

        // Status filter is handled by backend + local chips?
        // Current implementation uses 'filter' state for backend fetch.
        // But we also want local filtering if possible or just use the local chips to set the backend filter?
        // The user wants 'Status Chips' like ITProjects.
        // ITProjects uses local filtering on status. Tickets uses backend.
        // We will stick to backend fetching for 'closed' vs 'active', but handle sub-status locally if needed
        // For 'filter' state: 'all' (Active), 'closed' (Done).

        // Actually, let's keep the filter simple:
        // The status chips will update the 'filter' state which triggers fetch.

        return matchesSearch && matchesCountry && matchesAssign;
    });

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            
            {/* Modern Gradient Header */}
            <Grow in={true} timeout={500}>
                <Box sx={{ 
                    mb: 4, 
                    mt: 2, 
                    p: 4, 
                    borderRadius: 4, 
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                    color: 'white',
                    boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 3
                }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <ViewListIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                            Tickets Management
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Manage and track all support tickets
                        </Typography>
                    </Box>
                    
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => setOpen(true)}
                        sx={{ 
                            position: 'relative',
                            zIndex: 2,
                            borderRadius: 2, 
                            fontWeight: 'bold', 
                            px: 3, 
                            py: 1,
                            bgcolor: 'white',
                            color: '#1e3c72',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                            '&:hover': { bgcolor: '#f1f5f9', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                        }}
                    >
                        New Ticket
                    </Button>
                    
                    {/* Decorative background circles */}
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                </Box>
            </Grow>

            {/* Filters and Controls */}
            <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'center' }, gap: 2 }}>
                    
                            {/* Left: Filters */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Chip
                                    icon={<PersonIcon />}
                                    label="My Tickets"
                                    onClick={() => setAssignToMe(!assignedToMe)}
                                    sx={{ 
                                        fontWeight: 'bold', 
                                        borderRadius: 2, 
                                        px: 1,
                                        height: 32,
                                        bgcolor: assignedToMe ? '#eff6ff' : '#f8fafc',
                                        color: assignedToMe ? '#2563eb' : '#64748b',
                                        border: '1px solid',
                                        borderColor: assignedToMe ? '#bfdbfe' : '#e2e8f0',
                                        '&:hover': { bgcolor: assignedToMe ? '#dbeafe' : '#f1f5f9' },
                                        transition: 'all 0.2s ease'
                                    }}
                                />
                                <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 24, alignSelf: 'center' }} />
                                
                                {[
                                    { label: 'Active', value: 'all' },
                                    { label: 'Unassigned', value: 'unassigned' },
                                    { label: 'Closed', value: 'closed' }
                                ].map(f => (
                                    <Chip
                                        key={f.value}
                                        label={f.label}
                                        onClick={() => setFilter(f.value)}
                                        sx={{ 
                                            fontWeight: '600', 
                                            borderRadius: 2,
                                            height: 32,
                                            bgcolor: filter === f.value ? '#1e293b' : 'transparent',
                                            color: filter === f.value ? 'white' : '#64748b',
                                            '&:hover': { bgcolor: filter === f.value ? '#334155' : '#f1f5f9' },
                                            transition: 'all 0.2s ease'
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* Right: Actions */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <Select
                                        multiple
                                        value={selectedCountries}
                                        onChange={(e) => setSelectedCountries(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (selected.length === 0) {
                                                return <Typography color="textSecondary" sx={{ fontSize: '0.875rem' }}>All Countries</Typography>;
                                            }
                                            return selected.join(', ');
                                        }}
                                        sx={{ 
                                            borderRadius: 2, 
                                            bgcolor: '#f8fafc', 
                                            '& fieldset': { borderColor: '#e2e8f0' } 
                                        }}
                                    >
                                        <MenuItem disabled value="">
                                            <Typography variant="body2">Select Countries</Typography>
                                        </MenuItem>
                                        {countries.map(c => (
                                            <MenuItem key={c.id} value={c.name}>
                                                <Checkbox checked={selectedCountries.indexOf(c.name) > -1} size="small" />
                                                {c.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    placeholder="Search tickets..."
                                    size="small"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                                    }}
                                    sx={{ 
                                        flexGrow: 1, 
                                        minWidth: { sm: 250 },
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#f8fafc',
                                            borderRadius: 2,
                                            '& fieldset': { borderColor: '#e2e8f0' },
                                            '&:hover fieldset': { borderColor: '#cbd5e1' },
                                            '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                                        }
                                    }}
                                />

                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                                    size="small"
                                    sx={{ 
                                        height: 40,
                                        '& .MuiToggleButton-root': { 
                                            color: '#94a3b8',
                                            borderColor: '#e2e8f0',
                                            bgcolor: '#f8fafc',
                                            px: 2,
                                            '&.Mui-selected': {
                                                color: '#2563eb',
                                                bgcolor: '#eff6ff',
                                                borderColor: '#bfdbfe',
                                                '&:hover': { bgcolor: '#dbeafe' }
                                            },
                                            '&:hover': { bgcolor: '#f1f5f9' }
                                        }
                                    }}
                                >
                                    <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
                                    <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    </Card>

            {loading ? <Loading /> : (
                viewMode === 'grid' ? (
                    <Grid container spacing={3}>
                        {filteredTickets.length === 0 ? (
                            <Grid item xs={12}>
                                <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>No tickets found</Typography>
                            </Grid>
                        ) : filteredTickets.map((t) => (
                            <Grid item xs={12} md={4} lg={3} key={t.id}>
                                <Card
                                    sx={{ 
                                        height: '100%', 
                                        borderRadius: 4, 
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
                                        cursor: 'pointer', 
                                        transition: 'all 0.3s', 
                                        border: '1px solid #f1f5f9',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.1)', borderColor: '#e2e8f0' } 
                                    }}
                                    onClick={() => navigate(`/tickets/${t.id}`)}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'flex-start' }}>
                                            <Box sx={{ overflow: 'hidden', mr: 1 }}>
                                                <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                                    {t.id}
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: '800', fontSize: '1.1rem', color: '#1e293b', mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {t.title}
                                                </Typography>
                                                <Tooltip title={t.description || ''} arrow placement="top">
                                                    <Typography variant="body2" sx={{ color: '#64748b', mb: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {t.description || 'No description available'}
                                                    </Typography>
                                                </Tooltip>
                                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500 }}>
                                                    {t.type}
                                                </Typography>
                                                {t.requested_by && (
                                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                                        <PersonIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: '#94a3b8' }} />
                                                        {t.requested_by}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Chip
                                                label={t.status}
                                                size="small"
                                                color={getStatusColor(t.status)}
                                                onClick={(e) => handleStatusClick(e, t.id)}
                                                sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, flexShrink: 0, cursor: 'pointer' }}
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 3, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column' }}>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>CREATED</Typography>
                                                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                                                    <AccessTimeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: '#64748b' }} />
                                                    {t.created_at}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column' }}>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>DUE DATE</Typography>
                                                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>{t.due_date || 'N/A'}</Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                                            <Chip 
                                                size="small" 
                                                label={t.priority || 'Medium'} 
                                                variant="outlined"
                                                sx={{ 
                                                    height: 24, 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: 600,
                                                    color: t.priority === 'High' || t.priority === 'Critical' ? '#ef4444' : '#F59E0B',
                                                    borderColor: t.priority === 'High' || t.priority === 'Critical' ? '#fca5a5' : '#fcd34d',
                                                    bgcolor: t.priority === 'High' || t.priority === 'Critical' ? '#fef2f2' : '#fffbeb'
                                                }} 
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #f1f5f9' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>By: {t.created_by}</Typography>
                                            </Box>

                                            {t.assigned_to && t.assigned_to.length > 0 ? (
                                                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12, border: '2px solid white' } }}>
                                                    {t.assigned_to.map((u, i) => (
                                                        <Avatar key={u.id || i} alt={u.full_name} title={u.full_name} sx={{ bgcolor: '#3b82f6' }}>
                                                            {u.full_name ? u.full_name.charAt(0).toUpperCase() : '?'}
                                                        </Avatar>
                                                    ))}
                                                </AvatarGroup>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>Unassigned</Typography>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
                        <Table sx={{ minWidth: 650 }} aria-label="tickets table">
                            <TableHead sx={{ bgcolor: 'grey.50' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Country</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Assigned To</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTickets.map((t) => (
                                    <TableRow
                                        key={t.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                                        onClick={() => navigate(`/tickets/${t.id}`)}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                {t.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">{t.title}</Typography>
                                            {t.requested_by && <Typography variant="caption" color="textSecondary">Req: {t.requested_by}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={t.description || ''} arrow placement="top">
                                                <Typography variant="body2" color="textSecondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 200 }}>
                                                    {t.description || '-'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{t.type}</TableCell>
                                        <TableCell>{t.country_name || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={t.status}
                                                size="small"
                                                color={getStatusColor(t.status)}
                                                variant="outlined"
                                                onClick={(e) => handleStatusClick(e, t.id)}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(t.priority) }} />
                                                {t.priority || 'Medium'}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {t.assigned_to && t.assigned_to.length > 0 ? (
                                                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}>
                                                    {t.assigned_to.map((u, i) => (
                                                        <Avatar key={u.id || i} alt={u.full_name} src="/static/images/avatar/1.jpg" title={u.full_name}>
                                                            {u.full_name ? u.full_name.charAt(0).toUpperCase() : '?'}
                                                        </Avatar>
                                                    ))}
                                                </AvatarGroup>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">{t.created_at}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{t.due_date || 'N/A'}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredTickets.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body2" color="textSecondary">No tickets found matching your criteria.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            )}

            <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={handleStatusClose}
            >
                {['Open', 'In Progress', 'Reopen', 'Done'].map(s => (
                    <MenuItem key={s} onClick={() => handleStatusUpdate(s)}>{s}</MenuItem>
                ))}
            </Menu>

            <Dialog 
                open={open} 
                onClose={() => setOpen(false)} 
                maxWidth="md" 
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
            >
                <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Create New Ticket
                    <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Basic Information</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Title / Subject"
                                        fullWidth
                                        value={newTicket.title}
                                        onChange={e => setNewTicket({ ...newTicket, title: e.target.value })}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Description"
                                        value={newTicket.description}
                                        onChange={e => setNewTicket({ ...newTicket, description: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Problem"
                                        value={newTicket.problem || ''}
                                        onChange={e => setNewTicket({ ...newTicket, problem: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Recommendation"
                                        value={newTicket.recommendation || ''}
                                        onChange={e => setNewTicket({ ...newTicket, recommendation: e.target.value })}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Details & Timeline</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={newTicket.ticket_type_id}
                                            label="Type"
                                            onChange={e => setNewTicket({ ...newTicket, ticket_type_id: e.target.value })}
                                        >
                                            {types.map((t) => (
                                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Priority</InputLabel>
                                        <Select
                                            value={newTicket.priority}
                                            label="Priority"
                                            onChange={e => setNewTicket({ ...newTicket, priority: e.target.value })}
                                        >
                                            <MenuItem value="Low">Low</MenuItem>
                                            <MenuItem value="Medium">Medium</MenuItem>
                                            <MenuItem value="High">High</MenuItem>
                                            <MenuItem value="Critical">Critical</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Country</InputLabel>
                                        <Select
                                            value={newTicket.country_id}
                                            label="Country"
                                            onChange={e => setNewTicket({ ...newTicket, country_id: e.target.value })}
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {countries.map((c) => (
                                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="Due Date"
                                        value={newTicket.due_date}
                                        onChange={e => setNewTicket({ ...newTicket, due_date: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Assign To (Team)</InputLabel>
                                        <Select
                                            multiple
                                            value={newTicket.assigned_to_ids}
                                            onChange={(e) => {
                                                const { target: { value } } = e;
                                                setNewTicket({
                                                    ...newTicket,
                                                    assigned_to_ids: typeof value === 'string' ? value.split(',') : value,
                                                });
                                            }}
                                            input={<OutlinedInput label="Assign To (Team)" />}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => {
                                                        const user = users.find(u => u.id === value);
                                                        return <Chip key={value} label={user ? user.name : value} size="small" />;
                                                    })}
                                                </Box>
                                            )}
                                            MenuProps={MenuProps}
                                        >
                                            {users.map((user) => (
                                                <MenuItem key={user.id} value={user.id}>
                                                    <Checkbox checked={newTicket.assigned_to_ids.indexOf(user.id) > -1} />
                                                    <ListItemText primary={user.name} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Contact & Relations</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        freeSolo
                                        fullWidth
                                        options={adUsers}
                                        loading={adLoading}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            return option.displayName || option.name || '';
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            handleADSearch(newInputValue);
                                        }}
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <Box>
                                                    <Typography variant="body1">{option.displayName || option.name}</Typography>
                                                    {option.mail && <Typography variant="caption" color="textSecondary">{option.mail}</Typography>}
                                                    {option.country && <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>({option.country})</Typography>}
                                                </Box>
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Requested By (Azure AD)"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {adLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                        value={newTicket.requested_by}
                                        onChange={async (event, newValue) => {
                                            if (typeof newValue === 'string') {
                                                setNewTicket({ ...newTicket, requested_by: newValue });
                                                // Try to fetch from Azure AD if email-like
                                                if (newValue.includes('@')) {
                                                    try {
                                                        const res = await axios.get(`${API_URL}/tickets/check-ad-user?email=${newValue}`, { headers });
                                                        if (res.data.found && res.data.country_id) {
                                                            setNewTicket(prev => ({ ...prev, country_id: res.data.country_id }));
                                                        }
                                                    } catch (e) { console.log("AD Check failed", e); }
                                                }
                                            } else if (newValue) {
                                                const name = newValue.displayName || newValue.name;
                                                const email = newValue.mail || newValue.sender_email;

                                                let updates = { ...newTicket, requested_by: name, sender_email: email || newTicket.sender_email };

                                                // If AD user has country
                                                if (newValue.country) {
                                                    const matchedCountry = countries.find(c => c.name === newValue.country);
                                                    if (matchedCountry) {
                                                        updates.country_id = matchedCountry.id;
                                                    }
                                                }
                                                // If local user has countries
                                                else if (newValue.countries && newValue.countries.length > 0) {
                                                    const cName = newValue.countries[0];
                                                    const cObj = countries.find(c => c.name === cName);
                                                    if (cObj) {
                                                        updates.country_id = cObj.id;
                                                    }
                                                }

                                                setNewTicket(updates);
                                            } else {
                                                setNewTicket({ ...newTicket, requested_by: '' });
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Sender Email"
                                        value={newTicket.sender_email}
                                        onChange={e => setNewTicket({ ...newTicket, sender_email: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        fullWidth
                                        freeSolo
                                        options={adUsers}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            return option.displayName || option.name || '';
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            if (newInputValue && newInputValue.length >= 2) {
                                                handleADSearch(newInputValue);
                                            }
                                        }}
                                        filterOptions={(x) => x}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="On Behalf Name"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {adLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                        value={newTicket.on_behalf_name}
                                        onChange={async (event, newValue) => {
                                            if (typeof newValue === 'string') {
                                                setNewTicket({ ...newTicket, on_behalf_name: newValue });
                                            } else if (newValue) {
                                                const name = newValue.displayName || newValue.name;
                                                const email = newValue.mail || newValue.sender_email;

                                                let updates = { ...newTicket, on_behalf_name: name, on_behalf_email: email || newTicket.on_behalf_email };

                                                setNewTicket(updates);
                                            } else {
                                                setNewTicket({ ...newTicket, on_behalf_name: '' });
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="On Behalf Email"
                                        value={newTicket.on_behalf_email}
                                        onChange={e => setNewTicket({ ...newTicket, on_behalf_email: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        fullWidth
                                        options={projects}
                                        getOptionLabel={(option) => option.name || ''}
                                        value={projects.find(p => p.id === newTicket.project_id) || null}
                                        onChange={(event, newValue) => {
                                            setNewTicket({
                                                ...newTicket,
                                                project_id: newValue ? newValue.id : '',
                                                it_project_id: '' // Clear IT Project if regular project is selected
                                            });
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Related Project (Client)" />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        fullWidth
                                        options={itProjects}
                                        getOptionLabel={(option) => option.name || ''}
                                        value={itProjects.find(p => p.id === newTicket.it_project_id) || null}
                                        onChange={(event, newValue) => {
                                            setNewTicket({
                                                ...newTicket,
                                                it_project_id: newValue ? newValue.id : '',
                                                project_id: '' // Clear regular project if IT project is selected
                                            });
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Related IT Project" />}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => setOpen(false)} sx={{ borderRadius: 2, fontWeight: '600', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={createLoading} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1.5, boxShadow: '0 4px 14px rgba(30, 60, 114, 0.2)' }}>
                        {createLoading ? 'Creating...' : 'Create Ticket'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Tickets;
